/**
 * Shared, de-duplicated PhishTrainer play-settings fetch.
 *
 * The play home (`/play`) renders TWO independent components that each need the
 * workspace's PhishTrainer settings: `PlayerBrandLogo` (chrome → branding.logoPath)
 * and `ModePreferenceCard` (home → gameplay.mode.{allowUserToggle,defaultMode}).
 * Before this helper they fetched `/api/phishtrainer/settings` SEPARATELY, so
 * every play page made the settings request twice. The second (later) call fires
 * in the middle of the player-nav's RSC-prefetch burst and, on a small FE origin
 * (staging = 2 instances), intermittently returns a Cloudflare 502 — surfacing a
 * "Failed to load resource: 502" console error even though both callers degrade
 * gracefully (default logo / hidden mode card).
 *
 * Routing both through this module-level memoised fetch collapses the two
 * requests into ONE (the early caller wins; the later one reuses the in-flight /
 * cached promise), which removes the redundant load AND the duplicate-request
 * 502. The 30s TTL is long enough to dedupe a single page's concurrent mounts
 * yet short enough that a mid-session admin settings change is picked up on the
 * next interval. A failed fetch clears the memo so the next caller can retry.
 */
import { pfetch } from './play-api'

/** Shape we read from `/api/phishtrainer/settings` — kept loose on purpose; the
 *  proxy returns the full settings tree and callers pick the fields they need. */
export interface PlaySettingsResponse {
  data?: {
    branding?: { logoPath?: string }
    gameplay?: { mode?: { allowUserToggle?: boolean; defaultMode?: string } }
    [k: string]: unknown
  }
}

const TTL_MS = 30_000

let inflight: Promise<PlaySettingsResponse> | null = null
let fetchedAt = 0

/**
 * Fetch the play-portal settings once and share the result across all callers
 * for {@link TTL_MS}. Throws on a non-ok response (the memo is cleared so a
 * subsequent caller retries); callers are expected to `.catch` and fall back.
 */
export function getPlaySettings(): Promise<PlaySettingsResponse> {
  const now = Date.now()
  if (inflight && now - fetchedAt < TTL_MS) return inflight
  fetchedAt = now
  inflight = pfetch('/api/phishtrainer/settings')
    .then((r) => {
      if (!r.ok) throw new Error(`settings ${r.status}`)
      return r.json() as Promise<PlaySettingsResponse>
    })
    .catch((e) => {
      // Allow the next caller to retry rather than caching the failure.
      inflight = null
      throw e
    })
  return inflight
}
